import React, { useMemo } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { alpha, useTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';

import { toISODateInput } from '../utils/staffReportFormatters';
import { useStaffSplitLists } from 'sectors/pilates/staff/hooks/useStaffList';

/* ---------------- helpers ---------------- */
function staffLabel(s) {
  return s?.fullName || s?.staffName || s?.name || `#${s?.id ?? '—'}`;
}
function staffId(s) {
  return s?.staffId ?? s?.id ?? null;
}
function parseISODateLocal(yyyy_mm_dd, endOfDay = false) {
  if (!yyyy_mm_dd) return null;
  const [y, m, d] = yyyy_mm_dd.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return endOfDay ? new Date(y, m - 1, d, 23, 59, 59) : new Date(y, m - 1, d, 0, 0, 0);
}

/* ---------------- component ---------------- */
export function StaffFiltersCard({
  from,
  to,
  selectedStaff,
  onSelectStaff,
  loading,
  onChangeFrom,
  onChangeTo,
  onList,
}) {
  const theme = useTheme();
  const accent = '#A9D6F5';

  // ✅ daha minimal: daha az padding, input height sabit
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      borderRadius: 2,
      backgroundColor: alpha(theme.palette.common.white, 0.55),
      backdropFilter: 'blur(8px)',
      transition: 'all 160ms ease',
      '& fieldset': {
        borderColor: alpha(theme.palette.text.primary, 0.12),
        borderWidth: '1px',
      },
      '&:hover fieldset': { borderColor: alpha(theme.palette.text.primary, 0.16) },
      '&.Mui-focused fieldset': { borderColor: alpha(accent, 0.35) },
      '&.Mui-focused': { boxShadow: `0 0 0 4px ${alpha(accent, 0.1)}` },
    },
    '& .MuiInputLabel-root': { fontSize: 12, top: -2 }, // date label biraz yukarı
    '& .MuiFormHelperText-root': { display: 'none' }, // ✅ minimal: helper kapalı
  };

  const { workingList, passiveList, isLoading, isError } = useStaffSplitLists();

  const staffOptions = useMemo(() => {
    if (isError) return [];
    const w = (workingList || []).map((s) => ({ ...s, __group: 'Aktif' }));
    const p = (passiveList || []).map((s) => ({ ...s, __group: 'Pasif' }));
    return [...w, ...p];
  }, [workingList, passiveList, isError]);

  const disableAll = loading;

  return (
    <Card
      sx={{
        mt: 1, // ✅ daha az
        px: 1.25,
        py: 1,
        borderRadius: 2,
      }}
    >
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Başlangıç"
            type="date"
            value={toISODateInput(from) || ''}
            onChange={(e) => {
              const dt = parseISODateLocal(e.target.value, false);
              if (dt) onChangeFrom(dt);
            }}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            disabled={disableAll}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            size="small"
            label="Bitiş"
            type="date"
            value={toISODateInput(to) || ''}
            onChange={(e) => {
              const dt = parseISODateLocal(e.target.value, true);
              if (dt) onChangeTo(dt);
            }}
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            disabled={disableAll}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <Autocomplete
            size="small"
            options={staffOptions}
            value={selectedStaff || null}
            disabled={disableAll}
            onChange={(e, v) => onSelectStaff?.(v || null)}
            getOptionLabel={(o) => staffLabel(o)}
            isOptionEqualToValue={(o, v) => String(staffId(o)) === String(staffId(v))}
            groupBy={(o) => o?.__group || 'Diğer'}
            renderOption={(props, option) => (
              <li {...props} key={`staff-opt-${String(staffId(option))}`}>
                <MDBox
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    gap: 1,
                    py: 0.25,
                  }}
                >
                  <span style={{ fontWeight: 800, fontSize: 13 }}>{staffLabel(option)}</span>
                  {option?.role ? (
                    <Chip size="small" variant="outlined" label={String(option.role)} />
                  ) : null}
                </MDBox>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Eğitmen"
                placeholder="Seç..."
                sx={inputSx}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress size={14} /> : null}
                      {isError ? (
                        <Chip
                          size="small"
                          color="error"
                          variant="outlined"
                          label="Hata"
                          sx={{ mr: 0.5 }}
                        />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            slotProps={{
              paper: {
                sx: {
                  mt: 0.6,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                  backgroundColor: alpha(theme.palette.background.paper, 0.92),
                  backdropFilter: 'blur(12px)',
                  boxShadow: `0 12px 28px ${alpha(theme.palette.common.black, 0.12)}`,
                },
              },
              listbox: { sx: { p: 0.5 } },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={2}>
          <MDButton
            fullWidth
            size="small"
            variant="gradient"
            color="info"
            onClick={onList}
            disabled={loading}
            sx={{
              height: 32,
              minHeight: 32,
              fontSize: 12,
              px: 1,
            }}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Listele'}
          </MDButton>
        </Grid>
      </Grid>
    </Card>
  );
}
